import { z } from "zod";
import { COMPLEXITY_FACTOR_KEYS, LEVER_KEYS } from "./config.js";
import {
  BlockSchema,
  CostBlockSchema,
  CustomFactorSchema,
  CustomLeverSchema,
  EfficiencyBlockSchema,
  GoalSchema,
  IdSchema,
  LIMITS,
  OrgSchema,
  RevenueBlockSchema,
  type Block,
  type Goal,
  type ValueMap,
} from "./schema.js";

/**
 * Edit operations on a map (brief section 8.3). Operations address goals, blocks, levers and
 * factors by ID, never by array index, so two people editing at once do not shift each other's
 * targets. `applyOperation` is the only way a map changes, in the SPA (optimistically) and on the
 * server (authoritatively).
 *
 * Rules for concurrent edits (last write wins):
 * - Setting something that no longer exists is rejected (`not_found`).
 * - Removing something that no longer exists is a no-op, so a repeated remove is harmless.
 * - Adding with an ID that already exists is rejected (`duplicate`).
 * - `addGoal` with an anchor that has since been removed appends the goal at the end.
 */

const scalar = z.union([z.string().max(LIMITS.longText), z.number(), z.boolean()]);

export const OperationSchema = z.discriminatedUnion("type", [
  /**
   * A scalar field. No IDs: `org.name`, `org.facilitator`, `org.date` or `horizon`. With `goalId`:
   * a goal text field, `levers.<key>.on|measure` or `complexity.<factor>|dependsOn`. With `goalId`
   * and `blockId`: any input of that block except its ID and type.
   */
  z.object({
    type: z.literal("setField"),
    goalId: IdSchema.optional(),
    blockId: IdSchema.optional(),
    path: z.string().min(1).max(60),
    value: scalar,
  }),
  /** Appends the goal, or inserts it after `afterGoalId`. */
  z.object({ type: z.literal("addGoal"), goal: GoalSchema, afterGoalId: IdSchema.optional() }),
  z.object({ type: z.literal("removeGoal"), goalId: IdSchema }),
  /** Moves the goal after `afterGoalId`, or to the start when it is omitted. */
  z.object({ type: z.literal("moveGoal"), goalId: IdSchema, afterGoalId: IdSchema.optional() }),
  z.object({ type: z.literal("addBlock"), goalId: IdSchema, block: BlockSchema }),
  z.object({ type: z.literal("removeBlock"), goalId: IdSchema, blockId: IdSchema }),
  z.object({ type: z.literal("addCustomLever"), goalId: IdSchema, lever: CustomLeverSchema }),
  z.object({ type: z.literal("removeCustomLever"), goalId: IdSchema, leverId: IdSchema }),
  z.object({
    type: z.literal("setCustomLever"),
    goalId: IdSchema,
    leverId: IdSchema,
    field: z.enum(["name", "measure"]),
    value: z.string().max(LIMITS.shortText),
  }),
  z.object({ type: z.literal("addCustomFactor"), goalId: IdSchema, factor: CustomFactorSchema }),
  z.object({ type: z.literal("removeCustomFactor"), goalId: IdSchema, factorId: IdSchema }),
  z.object({
    type: z.literal("setCustomFactor"),
    goalId: IdSchema,
    factorId: IdSchema,
    field: z.enum(["label", "value"]),
    value: z.union([z.string().max(LIMITS.shortText), z.number()]),
  }),
]);

export type Operation = z.infer<typeof OperationSchema>;
export type OperationType = Operation["type"];

/**
 * Operations that remove something from the map. Only staff owners and editors may send them: client
 * editors can add and change goals, blocks, value areas and factors but not remove any of them
 * (brief 8.2; custom value areas and factors decided by Jason, 16 October 2026).
 */
export const REMOVE_OPERATIONS = [
  "removeGoal",
  "removeBlock",
  "removeCustomLever",
  "removeCustomFactor",
] as const satisfies readonly OperationType[];

export function isRemoval(op: Operation): boolean {
  return (REMOVE_OPERATIONS as readonly OperationType[]).includes(op.type);
}

export type OperationErrorCode = "not_found" | "invalid" | "limit" | "duplicate";

/** Raised when an operation cannot be applied. The message never includes field content. */
export class OperationError extends Error {
  readonly code: OperationErrorCode;
  constructor(code: OperationErrorCode, message: string) {
    super(message);
    this.name = "OperationError";
    this.code = code;
  }
}

const MAP_PATHS = new Set(["org.name", "org.facilitator", "org.date", "horizon"]);

const GOAL_TEXT_FIELDS = [
  "title",
  "stakeholder",
  "personalWin",
  "rightNow",
  "objective",
  "capability",
  "outcome",
  "costInaction",
] as const;

const GOAL_PATHS = new Set<string>([
  ...GOAL_TEXT_FIELDS,
  ...LEVER_KEYS.flatMap((k) => [`levers.${k}.on`, `levers.${k}.measure`]),
  ...COMPLEXITY_FACTOR_KEYS.map((k) => `complexity.${k}`),
  "complexity.dependsOn",
]);

const NOT_SETTABLE = new Set(["id", "type"]);
const BLOCK_PATHS: Record<Block["type"], ReadonlySet<string>> = {
  revenue: blockPaths(RevenueBlockSchema.shape),
  efficiency: blockPaths(EfficiencyBlockSchema.shape),
  cost: blockPaths(CostBlockSchema.shape),
};

function blockPaths(shape: Record<string, unknown>): ReadonlySet<string> {
  return new Set(Object.keys(shape).filter((k) => !NOT_SETTABLE.has(k)));
}

function fail(code: OperationErrorCode, message: string): never {
  throw new OperationError(code, message);
}

/** Parses with a schema; on failure reports where, never what, so content stays out of logs. */
function parse<S extends z.ZodType>(schema: S, value: unknown, what: string): z.infer<S> {
  const r = schema.safeParse(value);
  if (!r.success) {
    const where = r.error.issues[0]?.path.join(".") ?? "";
    fail("invalid", `Invalid ${what}${where ? ` at ${where}` : ""}`);
  }
  return r.data;
}

function setPath<T extends object>(obj: T, segments: readonly string[], value: unknown): T {
  const [head, ...rest] = segments;
  if (head === undefined) return obj;
  const current = (obj as Record<string, unknown>)[head];
  return {
    ...obj,
    [head]: rest.length > 0 ? setPath(current as object, rest, value) : value,
  };
}

function goalIndex(map: ValueMap, goalId: string): number {
  const i = map.goals.findIndex((g) => g.id === goalId);
  if (i < 0) fail("not_found", "Goal not found");
  return i;
}

function replaceGoal(map: ValueMap, goalId: string, change: (g: Goal) => Goal): ValueMap {
  const i = goalIndex(map, goalId);
  const before = map.goals[i] as Goal;
  const after = change(before);
  // An operation that changes nothing returns the same map, so callers can skip a revision.
  if (after === before) return map;
  const goals = map.goals.slice();
  goals[i] = after;
  return { ...map, goals };
}

function setMapField(map: ValueMap, path: string, value: unknown): ValueMap {
  if (!MAP_PATHS.has(path)) fail("invalid", "Unknown map field");
  if (path === "horizon") {
    const horizon = parse(z.number().int().min(1).max(5), value, "horizon");
    return { ...map, horizon };
  }
  const key = path.slice("org.".length) as keyof ValueMap["org"];
  const org = parse(OrgSchema, { ...map.org, [key]: value }, "organisation field");
  return { ...map, org };
}

function setGoalField(map: ValueMap, goalId: string, path: string, value: unknown): ValueMap {
  if (!GOAL_PATHS.has(path)) fail("invalid", "Unknown goal field");
  if (path === "complexity.dependsOn") {
    if (value === goalId) fail("invalid", "A goal cannot depend on itself");
    if (value !== "" && !map.goals.some((g) => g.id === value)) {
      fail("invalid", "Dependency goal not found");
    }
  }
  return replaceGoal(map, goalId, (g) =>
    parse(GoalSchema, setPath(g, path.split("."), value), "goal field"),
  );
}

function setBlockField(
  map: ValueMap,
  goalId: string,
  blockId: string,
  path: string,
  value: unknown,
): ValueMap {
  return replaceGoal(map, goalId, (g) => {
    const i = g.vcase.blocks.findIndex((b) => b.id === blockId);
    const block = g.vcase.blocks[i];
    if (!block) fail("not_found", "Block not found");
    if (!BLOCK_PATHS[block.type].has(path)) fail("invalid", "Unknown block field");
    const blocks = g.vcase.blocks.slice();
    blocks[i] = parse(BlockSchema, { ...block, [path]: value }, "block field");
    return { ...g, vcase: { blocks } };
  });
}

/** Replaces one entry of a goal's list (custom levers or factors) by ID. */
function mapListItem<T extends { id: string }>(
  list: readonly T[],
  id: string,
  what: string,
  change: (item: T) => T,
): T[] {
  const i = list.findIndex((x) => x.id === id);
  const item = list[i];
  if (!item) fail("not_found", `${what} not found`);
  const out = list.slice();
  out[i] = change(item);
  return out;
}

/**
 * Applies one operation and returns the new map. Pure: the input is never changed, and the same
 * input always gives the same output. Throws `OperationError` if the operation cannot be applied.
 * The operation is assumed to satisfy `OperationSchema` (the server parses every incoming one);
 * given that, the result satisfies `ValueMapSchema`.
 */
export function applyOperation(map: ValueMap, op: Operation): ValueMap {
  switch (op.type) {
    case "setField":
      if (op.blockId !== undefined) {
        if (op.goalId === undefined) fail("invalid", "A block field needs a goal ID");
        return setBlockField(map, op.goalId, op.blockId, op.path, op.value);
      }
      if (op.goalId !== undefined) return setGoalField(map, op.goalId, op.path, op.value);
      return setMapField(map, op.path, op.value);

    case "addGoal": {
      if (map.goals.length >= LIMITS.goals) fail("limit", "Too many goals");
      if (map.goals.some((g) => g.id === op.goal.id)) fail("duplicate", "Goal ID already exists");
      const goals = map.goals.slice();
      const anchor = op.afterGoalId ? goals.findIndex((g) => g.id === op.afterGoalId) : -1;
      goals.splice(anchor < 0 ? goals.length : anchor + 1, 0, op.goal);
      return { ...map, goals };
    }

    case "removeGoal": {
      if (!map.goals.some((g) => g.id === op.goalId)) return map;
      const goals = map.goals
        .filter((g) => g.id !== op.goalId)
        .map((g) =>
          g.complexity.dependsOn === op.goalId
            ? { ...g, complexity: { ...g.complexity, dependsOn: "" } }
            : g,
        );
      return { ...map, goals };
    }

    case "moveGoal": {
      const from = goalIndex(map, op.goalId);
      if (op.afterGoalId === op.goalId) return map;
      const goals = map.goals.slice();
      const [moved] = goals.splice(from, 1);
      const anchor = op.afterGoalId ? goals.findIndex((g) => g.id === op.afterGoalId) : -1;
      if (op.afterGoalId && anchor < 0) fail("not_found", "Anchor goal not found");
      goals.splice(anchor + 1, 0, moved as Goal);
      return { ...map, goals };
    }

    case "addBlock":
      return replaceGoal(map, op.goalId, (g) => {
        if (g.vcase.blocks.length >= LIMITS.blocksPerGoal) fail("limit", "Too many blocks");
        if (g.vcase.blocks.some((b) => b.id === op.block.id)) {
          fail("duplicate", "Block ID already exists");
        }
        return { ...g, vcase: { blocks: [...g.vcase.blocks, op.block] } };
      });

    case "removeBlock":
      return replaceGoal(map, op.goalId, (g) =>
        g.vcase.blocks.some((b) => b.id === op.blockId)
          ? { ...g, vcase: { blocks: g.vcase.blocks.filter((b) => b.id !== op.blockId) } }
          : g,
      );

    case "addCustomLever":
      return replaceGoal(map, op.goalId, (g) => {
        if (g.custom.length >= LIMITS.customLevers) fail("limit", "Too many value areas");
        if (g.custom.some((c) => c.id === op.lever.id)) {
          fail("duplicate", "Value area ID already exists");
        }
        return { ...g, custom: [...g.custom, op.lever] };
      });

    case "removeCustomLever":
      return replaceGoal(map, op.goalId, (g) =>
        g.custom.some((c) => c.id === op.leverId)
          ? { ...g, custom: g.custom.filter((c) => c.id !== op.leverId) }
          : g,
      );

    case "setCustomLever":
      return replaceGoal(map, op.goalId, (g) => ({
        ...g,
        custom: mapListItem(g.custom, op.leverId, "Value area", (c) => ({
          ...c,
          [op.field]: op.value,
        })),
      }));

    case "addCustomFactor":
      return replaceGoal(map, op.goalId, (g) => {
        if (g.complexity.custom.length >= LIMITS.customFactors) fail("limit", "Too many factors");
        if (g.complexity.custom.some((f) => f.id === op.factor.id)) {
          fail("duplicate", "Factor ID already exists");
        }
        return {
          ...g,
          complexity: { ...g.complexity, custom: [...g.complexity.custom, op.factor] },
        };
      });

    case "removeCustomFactor":
      return replaceGoal(map, op.goalId, (g) =>
        g.complexity.custom.some((f) => f.id === op.factorId)
          ? {
              ...g,
              complexity: {
                ...g.complexity,
                custom: g.complexity.custom.filter((f) => f.id !== op.factorId),
              },
            }
          : g,
      );

    case "setCustomFactor":
      return replaceGoal(map, op.goalId, (g) =>
        parse(
          GoalSchema,
          {
            ...g,
            complexity: {
              ...g.complexity,
              custom: mapListItem(g.complexity.custom, op.factorId, "Factor", (f) => ({
                ...f,
                [op.field]: op.value,
              })),
            },
          },
          "factor field",
        ),
      );
  }
}

/**
 * Applies operations in order. Stops at the first failure by throwing, leaving the caller's map
 * untouched.
 */
export function applyOperations(map: ValueMap, ops: readonly Operation[]): ValueMap {
  return ops.reduce(applyOperation, map);
}
